import React from 'react';
import { View, StyleSheet } from 'react-native';
import {
  Text,
  Card,
  Button,
  useTheme,
  ActivityIndicator,
  Chip,
} from 'react-native-paper';
import {
  Target,
  Clock,
  Star,
  Play,
  BookOpen,
  Coffee,
  Award,
  Brain,
} from 'lucide-react-native';
import type { RecommendedAction } from '@coffeeplexity/shared';
import { ConfidenceIndicator } from './ConfidenceIndicator';

interface TodaysFocusCardProps {
  action: RecommendedAction;
  onActionPress: (actionType: string, actionId?: string) => void;
  loading?: boolean;
}

export function TodaysFocusCard({
  action,
  onActionPress,
  loading = false,
}: TodaysFocusCardProps) {
  const theme = useTheme();

  // Get icon based on action type
  const getActionIcon = (type: string) => {
    switch (type) {
      case 'continue_journey':
        return Target;
      case 'try_recipe':
        return Coffee;
      case 'watch_video':
        return Play;
      case 'take_quiz':
        return Brain;
      case 'practice_skill':
        return Award;
      default:
        return BookOpen;
    }
  };

  const ActionIcon = getActionIcon(action.type);

  // Get priority color
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return '#FF6B35'; // Orange
      case 'medium':
        return '#FFC107'; // Amber/Yellow
      case 'low':
        return '#95A5A6'; // Gray
      default:
        return theme.colors.primary;
    }
  };

  const priorityColor = getPriorityColor(action.priority);

  // Format estimated time
  const formatTime = (minutes: number) => {
    if (minutes < 60) {
      return `${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0
      ? `${hours}h ${remainingMinutes}m`
      : `${hours}h`;
  };

  if (loading) {
    return (
      <Card
        style={[styles.container, { backgroundColor: theme.colors.surface }]}
      >
        <Card.Content style={styles.loadingContent}>
          <ActivityIndicator size='large' color={theme.colors.primary} />
          <Text
            variant='bodyMedium'
            style={[
              styles.loadingText,
              { color: theme.colors.onSurfaceVariant },
            ]}
          >
            Personalizing your focus...
          </Text>
        </Card.Content>
      </Card>
    );
  }

  return (
    <Card style={[styles.container, { backgroundColor: theme.colors.surface }]}>
      <Card.Content style={styles.content}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.titleContainer}>
            <ActionIcon size={20} color={priorityColor} />
            <Text
              variant='titleMedium'
              style={[styles.title, { color: theme.colors.onSurface }]}
            >
              Today's Focus
            </Text>
          </View>

          <Chip
            mode='outlined'
            textStyle={[styles.priorityText, { color: priorityColor }]}
            style={[
              styles.priorityChip,
              {
                borderColor: priorityColor,
                backgroundColor: (priorityColor as string) + '20',
              },
            ]}
          >
            {action.priority.toUpperCase()}
          </Chip>
        </View>

        {/* Action Details */}
        <View style={styles.actionContainer}>
          <Text
            variant='headlineSmall'
            style={[styles.actionTitle, { color: theme.colors.onSurface }]}
          >
            {action.title}
          </Text>

          <Text
            variant='bodyMedium'
            style={[
              styles.actionDescription,
              { color: theme.colors.onSurfaceVariant },
            ]}
          >
            {action.description}
          </Text>
        </View>

        {/* Metadata */}
        <View style={styles.metadata}>
          <View style={styles.metadataItem}>
            <Clock size={16} color={theme.colors.onSurfaceVariant} />
            <Text
              variant='bodySmall'
              style={[
                styles.metadataText,
                { color: theme.colors.onSurfaceVariant },
              ]}
            >
              {formatTime(action.estimatedMinutes)}
            </Text>
          </View>

          <ConfidenceIndicator
            confidence={action.confidence}
            size='small'
            showLabel={true}
          />
        </View>

        {/* AI Reasoning */}
        <View style={styles.reasoningContainer}>
          <View style={styles.reasoningHeader}>
            <Star size={14} color={theme.colors.primary} />
            <Text
              variant='labelMedium'
              style={[styles.reasoningLabel, { color: theme.colors.primary }]}
            >
              Why this matters
            </Text>
          </View>

          <Text
            variant='bodySmall'
            style={[styles.reasoning, { color: theme.colors.onSurfaceVariant }]}
          >
            {action.reasoning}
          </Text>
        </View>

        {/* Action Button */}
        <Button
          mode='contained'
          onPress={() =>
            onActionPress(action.type, action.contentId || action.activityId)
          }
          style={[styles.actionButton, { backgroundColor: priorityColor }]}
          contentStyle={styles.actionButtonContent}
          labelStyle={styles.actionButtonLabel}
        >
          Start Now
        </Button>
      </Card.Content>
    </Card>
  );
}

const styles = StyleSheet.create({
  container: {
    marginHorizontal: 16,
    marginBottom: 8,
  },
  content: {
    paddingVertical: 20,
  },
  loadingContent: {
    alignItems: 'center',
    paddingVertical: 32,
  },
  loadingText: {
    marginTop: 12,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  titleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  title: {
    fontWeight: '600',
  },
  priorityChip: {
    height: 22,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  priorityText: {
    fontSize: 10,
    fontWeight: '600',
    textAlign: 'center',
    lineHeight: 12,
  },
  actionContainer: {
    marginBottom: 16,
  },
  actionTitle: {
    fontWeight: '700',
    marginBottom: 8,
  },
  actionDescription: {
    lineHeight: 20,
  },
  metadata: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  metadataItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  metadataText: {
    fontSize: 12,
  },
  reasoningContainer: {
    backgroundColor: 'rgba(78, 205, 196, 0.1)', // Light teal background
    padding: 12,
    borderRadius: 8,
    marginBottom: 20,
  },
  reasoningHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 6,
  },
  reasoningLabel: {
    fontWeight: '600',
    fontSize: 12,
  },
  reasoning: {
    lineHeight: 16,
    fontSize: 12,
  },
  actionButton: {
    borderRadius: 8,
    minHeight: 44,
  },
  actionButtonContent: {
    paddingVertical: 8,
    paddingHorizontal: 16,
  },
  actionButtonLabel: {
    fontWeight: '600',
    fontSize: 16,
    lineHeight: 20,
    textAlign: 'center',
  },
});
